import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dumbbell, Activity, TrendingUp } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Analytics } from "@/lib/analytics";

interface ExerciseData {
  exercise_name: string;
  workout_date: string;
  weight_kg: number | null;
  sets: number | null;
  reps: number | null;
  distance_km: number | null;
  duration_seconds: number | null;
  workout_type: string;
}

export const ExerciseProgressCharts = () => {
  const { user } = useAuth();
  const [exerciseData, setExerciseData] = useState<ExerciseData[]>([]);
  const [selectedExercise, setSelectedExercise] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [uniqueExercises, setUniqueExercises] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>('weight');

  useEffect(() => {
    Analytics.progressChartViewed('exercise');
    if (user) {
      loadExerciseData();
    }
  }, [user]);

  // Real-time subscription for exercise updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('exercise-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'exercises'
        },
        () => {
          console.log('Exercise data changed, reloading...');
          loadExerciseData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'workouts'
        },
        (payload) => {
          // Only reload if a workout was completed
          if (payload.new && payload.new.completed_at) {
            console.log('Workout completed, reloading exercise data...');
            loadExerciseData();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const loadExerciseData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('exercises')
        .select(`
          name,
          sets,
          reps,
          weight_kg,
          distance_km,
          duration_seconds,
          workouts!inner(
            completed_at,
            created_at,
            workout_type,
            user_id
          )
        `)
        .eq('workouts.user_id', user.id)
        .not('workouts.completed_at', 'is', null)
        .order('completed_at', { referencedTable: 'workouts', ascending: true });


      if (error) throw error;

      const formattedData: ExerciseData[] = (data || []).map(exercise => ({
        exercise_name: exercise.name,
        workout_date: exercise.workouts.completed_at || exercise.workouts.created_at,
        weight_kg: exercise.weight_kg,
        sets: exercise.sets,
        reps: exercise.reps,
        distance_km: exercise.distance_km,
        duration_seconds: exercise.duration_seconds,
        workout_type: exercise.workouts.workout_type || 'strength'
      }));


      setExerciseData(formattedData);
      
      const exercises = [...new Set(formattedData.map(d => d.exercise_name))];
      setUniqueExercises(exercises);
      
      if (exercises.length > 0 && !selectedExercise) {
        setSelectedExercise(exercises[0]);
      }
    } catch (error) {
      console.error('Error loading exercise data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getExerciseProgressData = (exerciseName: string) => {
    return exerciseData
      .filter(d => d.exercise_name === exerciseName)
      .map((d, index) => {
        const dist = d.distance_km != null ? Number(d.distance_km) : 0;
        const dur = d.duration_seconds != null ? Number(d.duration_seconds) : 0;
        return {
          session: index + 1,
          date: new Date(d.workout_date).toLocaleDateString(),
          weight: d.weight_kg != null ? Number(d.weight_kg) : 0,
          sets: d.sets != null ? Number(d.sets) : 0,
          reps: d.reps != null ? Number(d.reps) : 0,
          distance: dist,
          pace: dist > 0 ? (dur / 60) / dist : 0,
          workout_type: d.workout_type
        };
      });
  };

  const selectedExerciseData = selectedExercise ? getExerciseProgressData(selectedExercise) : [];
  const isRunningExercise = selectedExerciseData.length > 0 && 
    (selectedExerciseData[0].workout_type?.toLowerCase() === 'running' || selectedExercise.toLowerCase().includes('running'));

  // Ensure an active tab is always visible and defaults based on exercise type
  useEffect(() => {
    setActiveTab(isRunningExercise ? 'distance' : 'weight');
  }, [selectedExercise, exerciseData, isRunningExercise]);

  const derivedActiveTab = isRunningExercise 
    ? (activeTab === 'distance' || activeTab === 'pace' ? activeTab : 'distance')
    : (activeTab === 'weight' || activeTab === 'sets' || activeTab === 'reps' ? activeTab : 'weight');

  if (loading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Loading exercise progress...
      </div>
    );
  }

  if (uniqueExercises.length === 0) {
    return (
      <Card className="bg-gradient-card border-border/50">
        <CardContent className="text-center py-8">
          <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
          <p className="text-muted-foreground">No exercise data available yet</p>
          <p className="text-sm text-muted-foreground">Complete some workouts to see your progress charts!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-card border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl font-normal">
          <TrendingUp className="h-5 w-5 text-primary" />
          Exercise Progress Charts
        </CardTitle>
        <div className="mt-4">
          <Select value={selectedExercise} onValueChange={setSelectedExercise}>
            <SelectTrigger className="w-full max-w-sm">
              <SelectValue placeholder="Select an exercise" />
            </SelectTrigger>
            <SelectContent>
              {uniqueExercises.map(exercise => (
                <SelectItem key={exercise} value={exercise}>
                  {exercise}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {selectedExerciseData.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No data available for this exercise</p>
          </div>
        ) : isRunningExercise ? (
          <Tabs value={derivedActiveTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="distance" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Distance
              </TabsTrigger>
              <TabsTrigger value="pace" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Pace
              </TabsTrigger>
            </TabsList>

            <TabsContent value="distance">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedExerciseData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      dataKey="date" 
                      label={{ value: 'Date', position: 'insideBottom', offset: -10 }}
                    />
                    <YAxis 
                      label={{ value: 'Distance (km)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      labelFormatter={(label) => label}
                      formatter={(value: number, name: string) => [
                        name === 'distance' ? `${value.toFixed(1)} km` : value,
                        name === 'distance' ? 'Distance' : name
                      ]}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="distance" 
                      stroke="hsl(var(--accent))" 
                      strokeWidth={3}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="pace">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedExerciseData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      dataKey="date" 
                      label={{ value: 'Date', position: 'insideBottom', offset: -10 }}
                    />
                    <YAxis 
                      label={{ value: 'Pace (min/km)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      labelFormatter={(label) => label}
                      formatter={(value: number) => [`${value.toFixed(2)} min/km`, 'Pace']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="pace" 
                      stroke="hsl(var(--gold))" 
                      strokeWidth={3}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <Tabs value={derivedActiveTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="weight" className="flex items-center gap-2">
                <Dumbbell className="h-4 w-4" />
                Weight
              </TabsTrigger>
              <TabsTrigger value="sets" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Sets
              </TabsTrigger>
              <TabsTrigger value="reps" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Reps
              </TabsTrigger>
            </TabsList>

            <TabsContent value="weight">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedExerciseData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      dataKey="date" 
                      label={{ value: 'Date', position: 'insideBottom', offset: -10 }}
                    />
                    <YAxis 
                      label={{ value: 'Weight (kg)', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      labelFormatter={(label) => label}
                      formatter={(value: number) => [`${value} kg`, 'Weight']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="weight" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={3}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="sets">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={selectedExerciseData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      dataKey="date" 
                      label={{ value: 'Date', position: 'insideBottom', offset: -10 }}
                    />
                    <YAxis 
                      label={{ value: 'Sets', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      labelFormatter={(label) => label}
                      formatter={(value: number) => [`${value}`, 'Sets']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="sets" 
                      fill="hsl(var(--gold))" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="reps">
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={selectedExerciseData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis 
                      dataKey="date" 
                      label={{ value: 'Date', position: 'insideBottom', offset: -10 }}
                    />
                    <YAxis 
                      label={{ value: 'Reps', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      labelFormatter={(label) => label}
                      formatter={(value: number) => [`${value}`, 'Reps']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="reps" 
                      fill="hsl(var(--accent))" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};